import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { X, MessageCircle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { WizardMessagesList } from "./WizardMessagesList";
import { WizardTypingIndicator } from "./components/WizardTypingIndicator";
import { WizardInput } from "./components/WizardInput";
import { WizardChatProps } from "./types";
import { useWizardStorage } from "./hooks/useWizardStorage";
import { useWizardMessages } from "./hooks/useWizardMessages";
import { useWizardSteps } from "./hooks/useWizardSteps";
import { useWizardProfile } from "./hooks/useWizardProfile";
import { useMobileDetection } from "./hooks/useMobileDetection";
import { startWelcomeConversation } from "./steps/welcomeStep";
import {
  handleProfileName,
  handleProfilePhoto,
  handleProfileDescription,
} from "./steps/profileStep";
import {
  handleServiceNameInput,
  handleServiceDurationInput,
} from "./steps/servicesStep";
import {
  handleStartTimeInput,
  handleEndTimeInput,
} from "./steps/availabilityStep";
import { handleLinksResponse } from "./steps/linksStep";
import { showFinishedStep } from "./steps/finishedStep";

function WizardChat({ onOpenChange }: Readonly<WizardChatProps>) {
  const { getUser, updateUser, refreshToken } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();

  // Estados locais
  const [isOpen, setIsOpen] = useState(false);

  // Hooks customizados
  const isMobile = useMobileDetection();
  const storage = useWizardStorage({ userId: user?.id });
  const { messages, isBotTyping, addBotMessage, addUserMessage } =
    useWizardMessages({
      userId: user?.id,
    });
  const { currentStep, setCurrentStep, setCompletedSteps, advanceToStep } =
    useWizardSteps({ persistState: storage.persistState });
  const { profileData, setProfileData, isProcessing, setIsProcessing } =
    useWizardProfile({
      userId: user?.id,
      isOpen,
    });
  const [inputValue, setInputValue] = useState("");
  const [awaitingUserInput, setAwaitingUserInput] = useState(false);

  // Refs
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const conversationStartedRef = useRef<boolean>(false);

  // Scroll automático
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, []);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen, scrollToBottom]);

  // Carregar estado salvo
  useEffect(() => {
    if (!user) return;
    const state = storage.loadState();
    if (state) {
      setCurrentStep(state.currentStepId);
      setCompletedSteps(state.completedSteps);
    }
  }, [user, storage, setCurrentStep, setCompletedSteps]);

  // Dependências para os steps (memoizado para evitar re-criação)
  const stepDeps = useMemo(
    () => ({
      userId: user?.id,
      profileData,
      addUserMessage,
      addBotMessage,
      advanceToStep,
      setProfileData,
      setIsProcessing,
      setAwaitingUserInput,
      saveCooldown: storage.saveCooldown,
      clearState: storage.clearState,
      markOnboardingCompleted: storage.markOnboardingCompleted,
      setIsOpen,
      navigate,
      locationSearch: location.search,
      conversationStartedRef,
      updateUser,
      refreshToken,
    }),
    [
      user?.id,
      profileData,
      addUserMessage,
      addBotMessage,
      advanceToStep,
      setProfileData,
      setIsProcessing,
      storage.saveCooldown,
      storage.clearState,
      storage.markOnboardingCompleted,
      location.search,
      updateUser,
      refreshToken,
      navigate,
    ],
  );

  // Handler para processar input do usuário no fluxo conversacional
  const handleUserInput = useCallback(async () => {
    if (!inputValue.trim() || !awaitingUserInput) return;

    const userInput = inputValue.trim();
    addUserMessage(userInput);
    setInputValue("");
    setAwaitingUserInput(false);

    // Processar baseado no step atual
    if (currentStep === "profile_name") {
      handleProfileName(userInput, stepDeps);
    } else if (currentStep === "profile_description") {
      handleProfileDescription(userInput, stepDeps);
    } else if (currentStep === "service_name") {
      handleServiceNameInput(userInput, stepDeps);
    } else if (currentStep === "service_duration") {
      handleServiceDurationInput(userInput, stepDeps);
    } else if (currentStep === "availability_start") {
      handleStartTimeInput(userInput, stepDeps);
    } else if (currentStep === "availability_end") {
      handleEndTimeInput(userInput, stepDeps);
    } else if (currentStep === "links") {
      handleLinksResponse(userInput, {
        ...stepDeps,
        showFinishedStep: () => void showFinishedStep(stepDeps as never),
      });
    }
  }, [inputValue, awaitingUserInput, currentStep, addUserMessage, stepDeps]);

  // Handler para processar upload de imagem
  const handleImageUpload = useCallback(
    (photoBase64: string | null) => {
      if (currentStep === "profile_photo") {
        handleProfilePhoto(photoBase64, stepDeps);
      }
    },
    [currentStep, stepDeps],
  );

  // Iniciar conversa quando abrir (apenas uma vez)
  useEffect(() => {
    if (!isOpen || !user) return;

    if (
      messages.length === 0 &&
      currentStep === "welcome" &&
      !conversationStartedRef.current
    ) {
      conversationStartedRef.current = true;
      startWelcomeConversation(stepDeps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user, messages.length, currentStep]);

  // Verificar parâmetro de URL para abrir ou se onboarding não foi completo
  useEffect(() => {
    if (!user || isOpen) return;

    const params = new URLSearchParams(location.search);
    const flag = params.get("onboarding");

    // Abre se tiver parâmetro onboarding na URL (força abertura, ignora cooldown)
    if (flag !== null && flag !== "0") {
      setIsOpen(true);
      return;
    }

    // Abre automaticamente se for profissional, não completou o onboarding e não está na lista de concluídos (localStorage)
    const completedInStorage =
      user?.id != null && storage.hasCompletedOnboarding(user.id);
    if (
      user?.is_professional &&
      user?.onboarding_completed !== true &&
      !completedInStorage
    ) {
      if (storage.canShowWizard()) {
        setIsOpen(true);
      }
    }
  }, [location.search, user, isOpen, storage]);

  // Notificar mudanças no estado de abertura
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  // Resetar estado apenas quando fechar
  useEffect(() => {
    if (!isOpen) {
      conversationStartedRef.current = false;
      if (messages.length === 0) {
        setCurrentStep("welcome");
      }
    }
  }, [isOpen, messages.length, setCurrentStep]);

  const handleClose = () => {
    storage.saveCooldown();
    const params = new URLSearchParams(location.search);
    params.delete("onboarding");
    navigate({ search: params.toString() }, { replace: true });
    setIsOpen(false);
    conversationStartedRef.current = false;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop para mobile */}
      {isMobile && (
        <button
          type="button"
          className="fixed inset-0 bg-black/50 z-40 md:hidden cursor-default"
          onClick={handleClose}
          aria-label="Fechar chat"
          tabIndex={-1}
        />
      )}

      {/* Chat container */}
      <div
        className="fixed left-0 right-0 bottom-0 z-50 bg-white dark:bg-gray-800 flex flex-col overflow-hidden transition-all duration-300 md:inset-auto md:bottom-6 md:right-6 md:rounded-lg md:shadow-2xl md:border md:border-gray-200 md:dark:border-gray-700 md:h-[700px] md:w-[480px]"
        style={
          isMobile
            ? {
                top: "max(60px, env(safe-area-inset-top))",
                height:
                  "calc(100dvh - max(60px, env(safe-area-inset-top)) - env(safe-area-inset-bottom))",
              }
            : undefined
        }
      >
        {/* Header */}
        <div className="bg-purple-600 text-white px-3 py-3 flex items-center justify-between border-b border-purple-700 flex-shrink-0 md:px-4 md:py-4">
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <div className="relative flex-shrink-0">
              <div className="w-9 h-9 rounded-full bg-purple-500 flex items-center justify-center md:w-10 md:h-10">
                <MessageCircle className="w-[18px] h-[18px] md:w-5 md:h-5" />
              </div>
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-purple-600 md:w-3 md:h-3"></span>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-sm md:text-base truncate">
                Primeiros Passos Easyfit
              </h3>
              <p className="text-[11px] text-purple-100 md:text-xs">Online</p>
            </div>
          </div>
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            <button
              onClick={handleClose}
              className="hover:bg-purple-700 active:bg-purple-800 p-1.5 rounded transition-colors md:p-2 touch-manipulation"
              aria-label="Fechar chat"
            >
              <X className="w-[18px] h-[18px] md:w-[18px] md:h-[18px]" />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 min-h-0 flex flex-col bg-gray-50 dark:bg-gray-900">
          <WizardMessagesList
            messages={messages}
            messagesContainerRef={messagesContainerRef}
            messagesEndRef={messagesEndRef}
            currentStep={currentStep}
            onImageUpload={handleImageUpload}
            profileData={profileData}
          />

          {/* Typing indicator */}
          {isBotTyping && <WizardTypingIndicator />}

          {/* Input Area */}
          {awaitingUserInput && (
            <WizardInput
              value={inputValue}
              onChange={setInputValue}
              onSubmit={handleUserInput}
              isProcessing={isProcessing}
              isBotTyping={isBotTyping}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default WizardChat;
